import { useState, useEffect, useCallback, Fragment } from 'react';
import { Loader2, Save, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import {
  getPlans,
  getFeatureRegistry,
  updatePlanFeatures,
  PlanTier,
  FeatureRegistry,
} from '@/services/plansApi';

export default function PlansPage() {
  const [plans, setPlans] = useState<PlanTier[]>([]);
  const [registry, setRegistry] = useState<FeatureRegistry | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [dirty, setDirty] = useState<Record<string, string[]>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [plansData, registryData] = await Promise.all([getPlans(), getFeatureRegistry()]);
      setPlans(plansData);
      setRegistry(registryData);
    } catch {
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getFeatures = (plan: PlanTier): string[] => {
    return dirty[plan.id] ?? plan.features ?? [];
  };

  const hasModuleWildcard = (features: string[], module: string): boolean => {
    return features.includes(module);
  };

  const hasSubFeature = (features: string[], module: string, sub: string): boolean => {
    return features.includes(module) || features.includes(`${module}.${sub}`);
  };

  const toggleModule = (plan: PlanTier, module: string) => {
    const current = getFeatures(plan);
    const subs = registry?.subFeatures[module];

    if (hasModuleWildcard(current, module)) {
      // Remove module wildcard and all its sub-features
      const updated = current.filter(
        (f) => f !== module && !f.startsWith(`${module}.`),
      );
      setDirty((d) => ({ ...d, [plan.id]: updated }));
    } else {
      // Add module wildcard, remove individual sub-features (wildcard covers them)
      const withoutSubs = current.filter((f) => !f.startsWith(`${module}.`));
      setDirty((d) => ({
        ...d,
        [plan.id]: [...withoutSubs, module],
      }));
      // Auto-collapse sub-features when wildcard is set
      if (subs) {
        setExpanded((e) => ({ ...e, [module]: false }));
      }
    }
  };

  const toggleSubFeature = (plan: PlanTier, module: string, sub: string) => {
    const current = getFeatures(plan);
    const key = `${module}.${sub}`;

    // If the module wildcard is active, switch to explicit sub-features
    if (hasModuleWildcard(current, module)) {
      const allSubs = registry?.subFeatures[module] ?? [];
      const withoutModule = current.filter((f) => f !== module);
      // Add all subs except the one being toggled off
      const updated = [
        ...withoutModule,
        ...allSubs.filter((s) => s !== sub).map((s) => `${module}.${s}`),
      ];
      setDirty((d) => ({ ...d, [plan.id]: updated }));
      return;
    }

    if (current.includes(key)) {
      setDirty((d) => ({ ...d, [plan.id]: current.filter((f) => f !== key) }));
    } else {
      setDirty((d) => ({ ...d, [plan.id]: [...current, key] }));
    }
  };

  const isModuleChecked = (features: string[], module: string): boolean => {
    if (hasModuleWildcard(features, module)) return true;
    const subs = registry?.subFeatures[module];
    if (!subs) return features.includes(module);
    return subs.some((s) => features.includes(`${module}.${s}`));
  };

  const isModuleIndeterminate = (features: string[], module: string): boolean => {
    if (hasModuleWildcard(features, module)) return false;
    const subs = registry?.subFeatures[module];
    if (!subs) return false;
    const enabledCount = subs.filter((s) => features.includes(`${module}.${s}`)).length;
    return enabledCount > 0 && enabledCount < subs.length;
  };

  const savePlan = async (plan: PlanTier) => {
    const features = dirty[plan.id];
    if (!features) return;

    setSaving(plan.id);
    try {
      const updated = await updatePlanFeatures(plan.id, features);
      setPlans((prev) => prev.map((p) => (p.id === plan.id ? updated : p)));
      setDirty((d) => {
        const next = { ...d };
        delete next[plan.id];
        return next;
      });
      toast.success(`Plan "${plan.name}" updated`);
    } catch {
      toast.error(`Failed to update plan "${plan.name}"`);
    } finally {
      setSaving(null);
    }
  };

  if (loading || !registry) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const modulesWithoutSubs = registry.modules.filter(
    (m) => !registry.subFeatures[m],
  );
  const modulesWithSubs = registry.modules.filter(
    (m) => !!registry.subFeatures[m],
  );

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Plans & Features</h1>
        <p className="mt-1 text-sm text-gray-500">
          Configure which modules and sub-features are accessible per plan.
          A checked module grants access to all its sub-features (wildcard).
        </p>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                Feature
              </th>
              {plans.map((plan) => (
                <th
                  key={plan.id}
                  className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500"
                >
                  <div>{plan.name}</div>
                  <div className="text-[10px] font-normal text-gray-400">
                    {plan.pricing_model === 'flat'
                      ? `${plan.price_monthly_cents / 100}€/mo`
                      : `${plan.price_monthly_cents / 100}€/user/mo`}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {/* Modules without sub-features */}
            {modulesWithoutSubs.map((module) => (
              <tr key={module} className="hover:bg-gray-50">
                <td className="px-4 py-2 text-sm font-medium text-gray-700">{module}</td>
                {plans.map((plan) => {
                  const features = getFeatures(plan);
                  return (
                    <td key={plan.id} className="px-4 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={features.includes(module)}
                        onChange={() => toggleModule(plan, module)}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                  );
                })}
              </tr>
            ))}

            {/* Modules with sub-features */}
            {modulesWithSubs.map((module) => {
              const subs = registry.subFeatures[module];
              const isExpanded = expanded[module] ?? false;

              return (
                <Fragment key={module}>
                  {/* Module row */}
                  <tr className="bg-gray-50/50 hover:bg-gray-100/50">
                    <td className="px-4 py-2">
                      <button
                        onClick={() =>
                          setExpanded((e) => ({ ...e, [module]: !isExpanded }))
                        }
                        className="flex items-center gap-1 text-sm font-semibold text-gray-800"
                      >
                        <ChevronRight
                          className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                        />
                        {module}
                        <span className="ml-1 text-xs font-normal text-gray-400">
                          ({subs.length})
                        </span>
                      </button>
                    </td>
                    {plans.map((plan) => {
                      const features = getFeatures(plan);
                      const checked = isModuleChecked(features, module);
                      const indeterminate = isModuleIndeterminate(features, module);
                      return (
                        <td key={plan.id} className="px-4 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={checked}
                            ref={(el) => {
                              if (el) el.indeterminate = indeterminate;
                            }}
                            onChange={() => toggleModule(plan, module)}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                      );
                    })}
                  </tr>

                  {/* Sub-feature rows */}
                  {isExpanded &&
                    subs.map((sub) => (
                      <tr key={`${module}.${sub}`} className="hover:bg-gray-50">
                        <td className="pl-10 pr-4 py-1.5 text-sm text-gray-500">
                          {sub.replace(/_/g, ' ')}
                        </td>
                        {plans.map((plan) => {
                          const features = getFeatures(plan);
                          return (
                            <td key={plan.id} className="px-4 py-1.5 text-center">
                              <input
                                type="checkbox"
                                checked={hasSubFeature(features, module, sub)}
                                onChange={() => toggleSubFeature(plan, module, sub)}
                                className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                </Fragment>
              );
            })}
          </tbody>
          <tfoot className="border-t border-gray-200 bg-gray-50">
            <tr>
              <td className="px-4 py-3" />
              {plans.map((plan) => {
                const isDirty = !!dirty[plan.id];
                return (
                  <td key={plan.id} className="px-4 py-3 text-center">
                    <button
                      disabled={!isDirty || saving === plan.id}
                      onClick={() => savePlan(plan)}
                      className={`inline-flex items-center justify-center gap-2 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                        isDirty
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {saving === plan.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Save className="h-3.5 w-3.5" />
                      )}
                      Save
                    </button>
                  </td>
                );
              })}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
